import PrimaryButton from '@/components/Buttons/PrimaryButton';
import AttachmentRenderer from '@/components/evaluation/AttachmentRenderer';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Answer, Evaluation, Question as QuestionType } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { router } from '@inertiajs/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface QuestionProps {
    evaluation: Evaluation;
    question: QuestionType;
    attempt: {
        id: number;
        time_remaining_seconds: number | null;
        started_at: string;
    };
    current_question_index: number;
    total_questions: number;
    selected_answer_ids: number[];
    is_last_question: boolean;
    course: {
        slug: string;
    };
}

const QuestionContent: React.FC<QuestionProps> = ({
    evaluation,
    question,
    attempt,
    current_question_index,
    total_questions,
    selected_answer_ids,
    is_last_question,
    course,
}) => {
    const { t } = useTranslation();
    const allowMultipleAnswers = Boolean(question.allow_multiple_answers);
    const [selectedAnswerIds, setSelectedAnswerIds] = useState<number[]>(
        selected_answer_ids || []
    );
    const [timeRemaining, setTimeRemaining] = useState<number | null>(
        evaluation.time_limit_minutes
            ? evaluation.time_limit_minutes * 60
            : null
    );
    const questionNumber = current_question_index + 1;

    useEffect(() => {
        setSelectedAnswerIds(selected_answer_ids || []);
    }, [question.id, selected_answer_ids]);
    const pageTitle = `Pregunta ${questionNumber} - ${evaluation.title}`;

    useEffect(() => {
        if (!evaluation.time_limit_minutes) return;

        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev === null || prev <= 0) {
                    // Time's up - redirect to time up page
                    router.get(`/evaluations/${evaluation.id}/time-up/`);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [evaluation.id, evaluation.time_limit_minutes]);

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const getTimerColor = () => {
        if (!timeRemaining || !evaluation.time_limit_minutes)
            return 'text-gray-600';
        const totalSeconds = evaluation.time_limit_minutes * 60;
        const percentage = (timeRemaining / totalSeconds) * 100;

        if (percentage <= 10) return 'text-red-600';
        if (percentage <= 25) return 'text-yellow-600';
        return 'text-green-600';
    };

    const handleAnswerSelect = (answerId: number) => {
        if (!allowMultipleAnswers) {
            setSelectedAnswerIds([answerId]);
            return;
        }

        setSelectedAnswerIds((previous) => {
            if (previous.includes(answerId)) {
                return previous.filter((id) => id !== answerId);
            }
            return [...previous, answerId];
        });
    };

    const handleNext = () => {
        if (selectedAnswerIds.length === 0) {
            alert('Por favor selecciona una respuesta antes de continuar');
            return;
        }

        // Submit form data using Inertia POST
        router.post(
            `/evaluations/${evaluation.id}/questions/${current_question_index}/`,
            {
                answer_ids: selectedAnswerIds,
            }
        );
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen bg-gray-50">
                {/* Header with progress */}
                <div className="border-b border-gray-200 bg-white shadow-sm">
                    <div className="mx-auto max-w-4xl px-4 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-lg font-semibold text-gray-800">
                                    {evaluation.title}
                                </h1>
                                <p className="text-sm text-gray-600">
                                    {t('evaluation.question.questionProgress', {
                                        current: current_question_index + 1,
                                        total: total_questions,
                                    })}
                                </p>
                            </div>

                            {/* Timer */}
                            {evaluation.time_limit_minutes &&
                                timeRemaining !== null && (
                                    <div className="text-right">
                                        <div
                                            className={`text-2xl font-bold ${getTimerColor()}`}
                                        >
                                            {formatTime(timeRemaining)}
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            {t(
                                                'evaluation.question.timeRemaining'
                                            )}
                                        </p>
                                    </div>
                                )}
                        </div>

                        {/* Progress bar */}
                        <div className="mt-4">
                            <div className="h-2 w-full rounded-full bg-gray-200">
                                <div
                                    className="bg-cb-primary h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: `${((current_question_index + 1) / total_questions) * 100}%`,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Question content */}
                <div className="mx-auto max-w-4xl px-4 py-8">
                    <div className="rounded-lg bg-white p-8 shadow-lg">
                        {/* Question */}
                        <div className="mb-8">
                            <h2
                                className="mb-4 text-xl font-semibold text-gray-800"
                                dangerouslySetInnerHTML={{
                                    __html: cleanWordPressHtml(
                                        question.question
                                    ),
                                }}
                            />

                            {/* Question attachments */}
                            <AttachmentRenderer
                                imageUrl={question.image_url}
                                attachmentUrl={question.attachment_url}
                                altText={t('evaluation.question.questionImage')}
                            />
                        </div>

                        {/* Answer options */}
                        <div className="mb-8 space-y-3">
                            <p className="text-sm text-gray-600">
                                {allowMultipleAnswers
                                    ? t('evaluation.question.selectOneOrMore')
                                    : t('evaluation.question.selectOnlyOne')}
                            </p>
                            {question.answers?.map(
                                (answer: Answer, index: number) => (
                                    <div
                                        key={answer.id}
                                        onClick={() =>
                                            handleAnswerSelect(answer.id)
                                        }
                                        className={`flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-all duration-200 ${
                                            selectedAnswerIds.includes(answer.id)
                                                ? 'border-cb-primary bg-cb-light'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div
                                            className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border-2 ${
                                                selectedAnswerIds.includes(answer.id)
                                                    ? 'border-cb-primary bg-cb-primary'
                                                    : 'border-gray-300'
                                            } ${allowMultipleAnswers ? 'rounded' : 'rounded-full'}`}
                                        >
                                            {selectedAnswerIds.includes(answer.id) && (
                                                <div
                                                    className={`bg-white ${allowMultipleAnswers ? 'h-3 w-3 rounded-sm' : 'h-2 w-2 rounded-full'}`}
                                                />
                                            )}
                                        </div>

                                        <div className="flex-1">
                                            <div className="mb-1 flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-500">
                                                    {String.fromCharCode(
                                                        65 + index
                                                    )}
                                                    .
                                                </span>
                                            </div>
                                            <div className="mb-3 leading-relaxed text-gray-800">
                                                {answer.answer}
                                            </div>

                                            {/* Answer attachments */}
                                            <AttachmentRenderer
                                                imageUrl={answer.image_url}
                                                attachmentUrl={
                                                    answer.attachment_url
                                                }
                                                altText={t(
                                                    'evaluation.answer.answerImage'
                                                )}
                                            />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>

                        {/* Next button */}
                        <div className="flex justify-end">
                            <PrimaryButton
                                onClick={handleNext}
                                disabled={selectedAnswerIds.length === 0}
                                size="md"
                            >
                                {is_last_question
                                    ? t('evaluation.question.finish')
                                    : t('evaluation.question.next')}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Question: React.FC<QuestionProps> = (props) => {
    return (
        <ThemeWrapper>
            <QuestionContent {...props} />
        </ThemeWrapper>
    );
};

export default Question;
